import { Accordion, Box, Skeleton } from "@chakra-ui/react";
import { useState } from "react";
import { useIntl } from "react-intl";

import { RanaHeading } from "@nens/ranalib";
import { RanaMarkdown } from "@nens/ranalib";
import { useAutoScrollToBottom } from "@nens/react/hooks";

import { BASE_PAGE_OVERFLOW_ID } from "@/components/base-page";
import { RanaAccordionItem } from "@/components/rana-accordion-item";
import { ProcessRunFormCollection } from "@/features/project-process-run-form";
import { JobStatusOrCreating } from "@/types/job-status-or-creating";
import { jobIsRunning } from "@/utils/job-is-running";

import { ProcessRunResults } from "../project-process-run-results/process-run-results";

import { ProcessRunStatus } from "./components/process-run-status";
import { useIndex } from "./hooks/use-process-run-index";
import { ProcessRunProps } from "./types/process-props";

export function ProcessRun(props: ProcessRunProps) {
  const intl = useIntl();
  const { process, job } = props;

  const [isCreating, setIsCreating] = useState(false);
  const status: JobStatusOrCreating = job?.state.type ?? (isCreating ? "creating" : undefined);

  const { index, userSetIndex, toggleTopOpen, toggleMiddleOpen, toggleBottomOpen } = useIndex(
    props,
    status
  );

  // Auto scroll the page to the bottom when the job is running
  // and the user has not set the index themselves
  useAutoScrollToBottom(jobIsRunning(status) && !userSetIndex ? BASE_PAGE_OVERFLOW_ID : null);
  return (
    <>
      <Skeleton isLoaded={!!process}>
        <RanaHeading type="h2" my={2}>
          {process?.name}
        </RanaHeading>
        <Accordion allowToggle defaultIndex={status ? undefined : 0}>
          <RanaAccordionItem
            title={intl.formatMessage({
              id: "process.description",
              defaultMessage: "Description",
            })}
          >
            <Box maxW="prose" fontWeight="light">
              <RanaMarkdown readMoreHeight="md" markdown={process?.description ?? process?.name} />
            </Box>
          </RanaAccordionItem>
        </Accordion>
      </Skeleton>

      <Accordion index={index} mt={4}>
        <Skeleton isLoaded={!!process}>
          <RanaAccordionItem
            onButtonClick={toggleTopOpen}
            title={intl.formatMessage({
              id: "process.form",
              defaultMessage: "Process Runner",
            })}
          >
            {process ? (
              <ProcessRunFormCollection
                key={job?.id} // when job changes, remount the form collection because the form data is based on the job, so when the job changes we want to reset the form data
                {...props}
                status={status}
                setIsCreating={setIsCreating}
                process={process}
              />
            ) : (
              " "
            )}
          </RanaAccordionItem>
        </Skeleton>
        <Skeleton isLoaded={!!process}>
          <RanaAccordionItem
            onButtonClick={toggleMiddleOpen}
            title={intl.formatMessage({
              id: "process.status",
              defaultMessage: "Status",
            })}
            accordionPanelProps={{ p: 0 }}
          >
            {process ? <ProcessRunStatus {...props} status={status} process={process} /> : " "}
          </RanaAccordionItem>
        </Skeleton>
        <Skeleton isLoaded={!!process}>
          <RanaAccordionItem
            onButtonClick={toggleBottomOpen}
            title={intl.formatMessage({
              id: "process.results",
              defaultMessage: "Results",
            })}
          >
            {process
              ? job &&
                job.state.type === "completed" && (
                  <ProcessRunResults {...props} process={process} job={job} />
                )
              : " "}
          </RanaAccordionItem>
        </Skeleton>
      </Accordion>
    </>
  );
}
